import { isoDateToUrlDate } from 'common/UrlDate';
import { first, flatten, omit } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import { useEditableGroupOrderLazyQuery } from '../../gqlReactTypings.generated.d';
import Notifications from '../../shared/Notifications';
import { useDispatch, useSelector } from '../../store';
import { resetEditableOrder, setEditableOrder } from '../../store/cart/actions';
import { selectAddOnItems } from '../../store/cart/selectors';
import { useCachedZipCode } from '../../user-preferences/useCachedZipCode';

interface IUseGroupOrderCart {
  groupOrderId?: string;
  forceReload?: boolean;
}

export const useEditableGroupOrderCart = ({ groupOrderId, forceReload = false }: IUseGroupOrderCart) => {
  const [fetchOrder, { data, loading, error }] = useEditableGroupOrderLazyQuery();
  const [zipCode] = useCachedZipCode();

  const dispatch = useDispatch();

  const { activeShefId, recentlyAdded, addOnItems, stateOrderId } = useSelector(({ editableOrderCart: cart }) => ({
    activeShefId: cart.activeCartShefId,
    recentlyAdded: cart.recentlyAdded,
    addOnItems: selectAddOnItems(cart),
    stateOrderId: cart.orderId,
  }));

  const groupOrder = useMemo(() => first(data?.groupOrdersByIds), [data]);
  useEffect(() => {
    if (groupOrderId !== undefined && (forceReload || stateOrderId !== groupOrderId)) {
      fetchOrder({ variables: { groupOrderId, zipCode } });
      dispatch(setEditableOrder({ orderId: groupOrderId }));
    }
  }, [dispatch, fetchOrder, forceReload, groupOrderId, stateOrderId, zipCode]);

  useEffect(() => {
    if (groupOrder) {
      const firstOrder = first(groupOrder.orders);
      const now = new Date().toISOString();

      const lineItems = flatten(
        groupOrder.orders.map((order) =>
          order.lineItems.map((lineItem) => ({
            ...omit(lineItem, 'foodItem'),
            id: lineItem.foodItemId,
            shefId: order.publicShef.id,
            createdAt: now,
            updatedAt: now,
            customizationIds: lineItem.customizationIds,
          }))
        )
      );

      const editableOrder = {
        ready: true,
        groupOrderId: groupOrder.id,
        foodItems: flatten(groupOrder.orders.map((order) => order.lineItems.map(({ foodItem }) => foodItem))),
        isMultiCart: true,
        activeCartShefId: firstOrder?.publicShef.id,
        lineItems,
        addOnItems,
        recentlyAdded,
        orderType: groupOrder.orderType,
        shefTipPercent: null,
        rawOrderData: {
          ...firstOrder,
          groupOrderId: groupOrder.id,
          timeWindowId: groupOrder.timeWindowId,
          orderType: groupOrder.orderType,
          lineItems,
        },
        timeWindowId: groupOrder.timeWindowId,
        shefs: groupOrder.orders.map((order) => ({
          ...order.publicShef,
          imageUrl: order.publicShef.imageUrl || '',
          syncedAt: now,
          loading: false as const,
          name: order.publicShef.firstName || '',
          deliveryDate: order.deliveryDate,
        })),
        deliveryDate: isoDateToUrlDate(groupOrder.deliveryDate),
        promoApplied: groupOrder.promoCodes[0]?.value,
        promoCode: groupOrder.promoCodes[0]?.code,
      };
      dispatch(setEditableOrder(editableOrder));
    }
  }, [data, dispatch]);

  const menuItemsByShefId = useMemo(
    () =>
      data?.groupOrdersByIds[0]?.orders.reduce((acc, order) => {
        acc[order.publicShef.id] = order.publicShef.activeFoodItems;
        return acc;
      }, {}),
    [data]
  );

  const resetOrderState = useCallback(() => {
    dispatch(resetEditableOrder());
    Notifications.info('Dish editing has been cancelled. No changes to your order were saved.');
  }, [dispatch]);

  const deliveryDate = DateTime.fromISO(groupOrder?.deliveryDate ?? '', {
    zone: groupOrder?.orders[0].publicShef.shefProfile.timeZone,
  });
  return {
    groupOrder,
    loading,
    error,
    deliveryDate,
    menuItemsByShefId,
    activeShefId,
    resetOrderState,
  };
};
